import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useParams, useNavigate } from "react-router-dom";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { SLABadge } from "@/components/SLABadge";
import { STATUSES, STATUS_LABELS, fmtDateTime } from "@/lib/constants";
import { SignaturePad } from "@/components/SignaturePad";
import { ArrowLeft, MapPin, Phone, Building2, Cpu, Camera, Signature, Plus, Trash2 } from "lucide-react";

export default function EngineerJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [spares, setSpares] = useState([]);
  const [note, setNote] = useState("");
  const [spareForm, setSpareForm] = useState({ spare_id: "", part_name: "", quantity: 1, unit_price: 0, remarks: "" });
  const [err, setErr] = useState("");
  const [signingOpen, setSigningOpen] = useState(false);
  const sigRef = useRef(null);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([api.get(`/tickets/${id}`), api.get("/spares")]);
      setTicket(t.data); setSpares(s.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const setStatus = async (s) => {
    try { await api.post(`/tickets/${id}/status`, { status: s }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const addNote = async () => { if (!note.trim()) return; try { await api.post(`/tickets/${id}/notes`, { note }); setNote(""); await load(); } catch (e) { setErr(formatApiError(e)); } };
  const pickSpare = (sid) => {
    if (!sid) return setSpareForm((f) => ({ ...f, spare_id: "", part_name: "" }));
    const s = spares.find((x) => x.id === sid);
    if (!s) return;
    setSpareForm((f) => ({ ...f, spare_id: sid, part_name: s.part_name }));
  };
  const addSpareItem = async () => {
    if (!spareForm.part_name.trim() && !spareForm.spare_id) return;
    try {
      await api.post(`/tickets/${id}/spare-items`, {
        spare_id: spareForm.spare_id || null,
        part_name: spareForm.part_name.trim() || null,
        quantity: Math.max(1, Number(spareForm.quantity) || 1),
        unit_price: Number(spareForm.unit_price) || 0,
        remarks: spareForm.remarks.trim() || null,
      });
      setSpareForm({ spare_id: "", part_name: "", quantity: 1, unit_price: 0, remarks: "" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const removeSpareItem = async (idx) => {
    try { await api.delete(`/tickets/${id}/spare-items/${idx}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  if (!ticket) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="px-4 py-4">
      <button onClick={() => navigate(-1)} className="mb-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>

      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-muted-foreground">{ticket.ticket_no}</span>
          <PriorityBadge priority={ticket.priority} />
          <SLABadge status={ticket.sla_status} remainingMin={ticket.sla_remaining_min} />
        </div>
        <h1 className="mt-1 font-heading text-xl font-bold leading-tight">{ticket.complaint}</h1>
      </div>

      <div className="sharp-card mb-3 p-4">
        <div className="mb-2 flex items-center gap-2">
          <StatusBadge status={ticket.status} />
        </div>
        <label className="label-eyebrow mb-1 block">Update status</label>
        <select data-testid="engineer-job-status" value={ticket.status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button data-testid="btn-on-way" onClick={() => setStatus("on_the_way")} className="rounded-md border border-border/70 py-2 text-xs font-semibold">On the Way</button>
          <button data-testid="btn-reached" onClick={() => setStatus("reached")} className="rounded-md border border-border/70 py-2 text-xs font-semibold">Reached</button>
          <button data-testid="btn-complete" onClick={() => setStatus("completed")} className="rounded-md bg-emerald-500/20 py-2 text-xs font-semibold text-emerald-400">Complete</button>
        </div>
      </div>

      <div className="sharp-card mb-3 p-4">
        <p className="label-eyebrow flex items-center gap-2"><Building2 className="h-3.5 w-3.5" /> Customer</p>
        <p className="mt-2 font-medium">{ticket.customer?.company_name}</p>
        <p className="text-xs text-muted-foreground">{ticket.contact_person}</p>
        <div className="mt-3 flex gap-2">
          {ticket.contact_phone && (
            <a href={`tel:${ticket.contact_phone}`} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border/70 py-2 text-xs font-semibold">
              <Phone className="h-3.5 w-3.5" /> Call
            </a>
          )}
          {ticket.customer?.address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ticket.customer.address)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border/70 py-2 text-xs font-semibold"
            >
              <MapPin className="h-3.5 w-3.5" /> Navigate
            </a>
          )}
        </div>
      </div>

      <div className="sharp-card mb-3 p-4">
        <p className="label-eyebrow">Description</p>
        <p className="mt-2 whitespace-pre-wrap text-sm">{ticket.description || "—"}</p>
      </div>

      <div className="sharp-card mb-3 p-4">
        <p className="label-eyebrow">On-site evidence</p>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(ticket.attachments || []).map((a) => (
            <EngineerAttachmentThumb key={a.file_id} att={a} />
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-border/70 py-2 text-xs font-semibold hover:bg-secondary">
            <Camera className="h-3.5 w-3.5" /> Add photo
            <input
              data-testid="engineer-photo-input"
              type="file" accept="image/*" capture="environment" className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const fd = new FormData();
                fd.append("file", f); fd.append("kind", "photo"); fd.append("ticket_id", id);
                try { await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
                catch (err) { setErr(formatApiError(err)); }
                finally { e.target.value = ""; }
              }}
            />
          </label>
          <button
            type="button"
            data-testid="engineer-signature-open"
            onClick={() => setSigningOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border/70 py-2 text-xs font-semibold hover:bg-secondary"
          >
            <Signature className="h-3.5 w-3.5" /> Customer sign-off
          </button>
        </div>

        {signingOpen && (
          <div className="mt-3 rounded-md border border-border/60 p-2">
            <p className="mb-2 label-eyebrow">Customer signature</p>
            <SignaturePad
              ref={sigRef}
              onDone={async (blob) => {
                if (!blob) return;
                const fd = new FormData();
                fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
                fd.append("kind", "signature"); fd.append("ticket_id", id);
                try {
                  await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  setSigningOpen(false);
                  await load();
                } catch (er) { setErr(formatApiError(er)); }
              }}
            />
            <button onClick={() => setSigningOpen(false)} className="mt-2 w-full rounded-md border border-border/70 py-1.5 text-[11px] text-muted-foreground">Cancel</button>
          </div>
        )}
      </div>

      <div className="sharp-card mb-3 p-4">
        <p className="label-eyebrow flex items-center gap-2"><Cpu className="h-3.5 w-3.5" /> Spare Item</p>
        <div className="mt-2 space-y-1.5">
          {(ticket.spare_items || []).map((p, i) => (
            <div key={i} data-testid={`engineer-spare-row-${i}`} className="group flex items-center justify-between gap-2 rounded-md border border-border/60 px-2 py-1.5 text-xs">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{p.part_name}</p>
                <p className="text-[10px] text-muted-foreground">
                  ×{p.quantity}{Number(p.unit_price) > 0 ? ` · ₹${Number(p.unit_price).toFixed(2)}` : ""}
                  {p.source === "manual" ? " · manual" : ""}
                </p>
              </div>
              <button
                data-testid={`engineer-spare-remove-${i}`}
                onClick={() => removeSpareItem(i)}
                className="rounded-md border border-border/60 p-1 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {(!ticket.spare_items || ticket.spare_items.length === 0) && <p className="text-xs text-muted-foreground">None</p>}
        </div>
        <div className="mt-3 space-y-2 rounded-md border border-dashed border-border/60 p-2">
          <select
            data-testid="engineer-spare-picker"
            value={spareForm.spare_id}
            onChange={(e) => pickSpare(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          >
            <option value="">— Manual entry —</option>
            {spares.map((s) => <option key={s.id} value={s.id}>{s.part_id} · {s.part_name}</option>)}
          </select>
          <input
            data-testid="engineer-spare-name"
            value={spareForm.part_name}
            onChange={(e) => setSpareForm({ ...spareForm, part_name: e.target.value })}
            placeholder="Part name *"
            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="engineer-spare-qty"
              type="number" min="1" value={spareForm.quantity}
              onChange={(e) => setSpareForm({ ...spareForm, quantity: e.target.value })}
              placeholder="Qty"
              className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            />
            <input
              data-testid="engineer-spare-price"
              type="number" min="0" step="0.01" value={spareForm.unit_price}
              onChange={(e) => setSpareForm({ ...spareForm, unit_price: e.target.value })}
              placeholder="₹ Unit price"
              className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            />
          </div>
          <input
            data-testid="engineer-spare-remarks"
            value={spareForm.remarks}
            onChange={(e) => setSpareForm({ ...spareForm, remarks: e.target.value })}
            placeholder="Remarks"
            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          />
        </div>
        <button data-testid="engineer-spare-add" onClick={addSpareItem} className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary py-2 text-xs font-semibold text-primary-foreground">
          <Plus className="h-3.5 w-3.5" /> Add spare item
        </button>
      </div>

      <div className="sharp-card mb-3 p-4">
        <p className="label-eyebrow">Notes</p>
        <div className="mt-2 space-y-1.5">
          {(ticket.notes || []).map((n, i) => (
            <div key={i} className="rounded-md border border-border/60 bg-secondary/30 px-2 py-1.5 text-xs">
              <p className="text-[10px] text-muted-foreground">{n.by_name} · {fmtDateTime(n.at)}</p>
              <p>{n.note}</p>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <input data-testid="engineer-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note…" className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
          <button data-testid="engineer-note-submit" onClick={addNote} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Add</button>
        </div>
      </div>

      {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
    </div>
  );
}

function EngineerAttachmentThumb({ att }) {
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/files/${att.file_id}`, { responseType: "blob" });
        if (alive) setBlob(URL.createObjectURL(r.data));
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [att.file_id]);
  const isImage = (att.content_type || "").startsWith("image/");
  return (
    <div className="overflow-hidden rounded-md border border-border/60">
      {isImage && blob ? (
        <img src={blob} alt={att.filename} className="aspect-square w-full object-cover" />
      ) : (
        <div className="flex aspect-square items-center justify-center bg-secondary/40">
          <Cpu className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
      <div className="border-t border-border/60 px-1.5 py-1 text-center text-[9px] uppercase tracking-wider text-muted-foreground">
        {att.kind}
      </div>
    </div>
  );
}
